// Pure helpers for the Purchase Return form.

export const RETURN_REASONS = [
  { value: 'defective', label: 'Defective' },
  { value: 'damaged', label: 'Damaged in Transit' },
  { value: 'colour_fade', label: 'Colour Fade' },
  { value: 'shrinkage', label: 'Shrinkage' },
  { value: 'stitching_issue', label: 'Stitching Issue' },
  { value: 'neck_issue', label: 'Neck Issue' },
  { value: 'bubble_after_wash', label: 'Bubble After Wash' },
  { value: 'hole', label: 'Hole' },
  { value: 'tearing', label: 'Tearing' },
  { value: 'not_moving', label: 'Not Moving' },
  { value: 'wrong_item', label: 'Wrong Item' },
  { value: 'excess', label: 'Excess Quantity' },
  { value: 'other', label: 'Other' },
];

export const SIZE_ORDER = ['S', 'M', 'L', 'XL', '2XL', 'XXL', '3XL', 'XXXL', '4XL', '5XL', 'Free', 'Free Size'];

export function createEmptyRow(sr) {
  return {
    sr, product_id: '', design_code: '', designSearch: '',
    color: '', size: '', qty: '', rate: 0, reason: 'defective', notes: '',
    colors: [], sizes: [], sizeGroups: [], individualSizes: [],
    product: null, showDesignDropdown: false,
    source_bill: '', source_bill_date: '', source_entry_id: '',
    pe_variants: [],
    box_number: 1,
  };
}

// Build size groups by WSP for a product+color
export function getSizeGroups(product, color, fallbackSizes) {
  if (!product || !color) return { groups: [], individualSizes: [] };
  const skus = product.skus?.filter(s => s.color === color) || [];
  const order = (s) => { const i = SIZE_ORDER.indexOf(s); return i === -1 ? 999 : i; };
  if (skus.length === 0 && fallbackSizes && fallbackSizes.length > 0) {
    const sorted = [...fallbackSizes].sort((a, b) => order(a) - order(b));
    return { groups: [], individualSizes: sorted.map(s => ({ size: s, rate: 0 })) };
  }
  const byPrice = {};
  skus.forEach(s => {
    const price = s.wsp || 0;
    if (!byPrice[price]) byPrice[price] = [];
    byPrice[price].push(s.size);
  });
  const groups = Object.entries(byPrice)
    .filter(([, sizes]) => sizes.length > 1)
    .map(([price, sizes]) => ({
      id: `set_${sizes[0]}_${sizes[sizes.length - 1]}`,
      label: `Set: ${sizes[0]}-${sizes[sizes.length - 1]}`,
      sizes, rate: parseFloat(price), count: sizes.length,
    }));
  const individualSizes = skus.map(s => ({ size: s.size, rate: s.wsp || 0 }));
  // For forwarding designs with partial SKUs, merge with fallback sizes
  if (fallbackSizes && fallbackSizes.length > 0) {
    const skuSizeSet = new Set(individualSizes.map(s => s.size));
    for (const sz of fallbackSizes) {
      if (!skuSizeSet.has(sz)) individualSizes.push({ size: sz, rate: 0 });
    }
    individualSizes.sort((a, b) => order(a.size) - order(b.size));
  }
  return { groups, individualSizes };
}

export function sortScannedRows(rows) {
  const filled = rows.filter(r => r.product_id);
  const empty = rows.filter(r => !r.product_id);
  filled.sort((a, b) => {
    const dA = (a.design_code || '').toUpperCase(), dB = (b.design_code || '').toUpperCase();
    if (dA !== dB) return dA.localeCompare(dB, undefined, { numeric: true });
    const cA = (a.color || '').toUpperCase(), cB = (b.color || '').toUpperCase();
    if (cA !== cB) return cA.localeCompare(cB);
    const sA = SIZE_ORDER.indexOf(a.size), sB = SIZE_ORDER.indexOf(b.size);
    return (sA === -1 ? 999 : sA) - (sB === -1 ? 999 : sB);
  });
  return [...filled.map((r, i) => ({ ...r, sr: i + 1 })), ...empty.map((r, i) => ({ ...r, sr: filled.length + i + 1 }))];
}

export function computeTotals(rows) {
  const grandQty = rows.reduce((s, r) => {
    const q = parseInt(r.qty) || 0;
    if (r.size === 'both') {
      return s + (r.sizeGroups || []).reduce((sum, g) => sum + q * g.count, 0);
    }
    const grp = (r.sizeGroups || []).find(g => g.id === r.size);
    return s + (grp ? q * grp.count : q);
  }, 0);
  const grandAmount = rows.reduce((s, r) => {
    const q = parseInt(r.qty) || 0;
    const rate = r.rate || 0;
    if (r.size === 'both') {
      return s + (r.sizeGroups || []).reduce((sum, g) => sum + q * g.count * (g.rate || rate), 0);
    }
    const grp = (r.sizeGroups || []).find(g => g.id === r.size);
    return s + (grp ? q * grp.count * rate : q * rate);
  }, 0);
  return { grandQty, grandAmount };
}
